import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { getCurrentDate } from "../../../configs/helpers";
import { fetchedWarehousesBySubsidiary } from "../../../apis/apis.logistics/apisProduct";
import { fetchedStockInTwoWarehouses } from "../../../apis/apis.buys/apisPurchase";
import { sendDevolutionData, fetchedVehiclesBySubsidiary } from "../../../apis/apis.dispatches/apisDispatch";
import { ContextAuth } from "../../../contexts/ContextAuth";

const initialState = {
    warehouseID: 0, vehicleID: 0, vehicleTwoID: 0,
    warehouseName: "", vehicleLicensePlate: "", vehicleTwoLicensePlate: "",
    readjustmentMode: "E", readjustmentMotive: "",
    supplyingDate: getCurrentDate("-"), userID: 0,
    details: [], comparatives: [],
};

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";
const thCls    = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300";
const tdCls    = "px-3 py-2 text-xs border-b border-gray-100 dark:border-gray-700 align-middle";

function warehouseLabel(item) {
    return item.gang.gangID === 0
        ? item.warehouseName
        : `${item.warehouseName} | ${item.gang.gangName}`;
}

export function DevolutionPage() {
    const [devolutionState, setDevolutionState] = useState(initialState);
    const [vehicles, setVehicles]               = useState([]);
    const [warehouses, setWarehouses]           = useState([]);
    const [isSaving, setIsSaving]               = useState(false);
    const { auth } = useContext(ContextAuth);

    const getWarehouses = async (id) => {
        try { setWarehouses(await fetchedWarehousesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };
    const getVehicles = async (id) => {
        try { setVehicles(await fetchedVehiclesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };
    const getComparatives = async (vehicleID, warehouseID) => {
        try {
            const response = await fetchedStockInTwoWarehouses({ warehouseID: vehicleID, vehicleID: warehouseID });
            setDevolutionState((s) => ({ ...s, comparatives: Object.values(response) }));
        } catch (e) { throw e; }
    };

    useEffect(() => {
        getWarehouses(auth.data.subsidiary.subsidiary_id);
        getVehicles(auth.data.subsidiary.subsidiary_id);
    }, []);

    useEffect(() => {
        if (Number(devolutionState.warehouseID) > 0 && Number(devolutionState.vehicleID) > 0)
            getComparatives(devolutionState.vehicleID, devolutionState.warehouseID);
    }, [devolutionState.warehouseID, devolutionState.vehicleID]);

    const handleClickBtnSave = async () => {
        if (!devolutionState.comparatives.length) { toast.error("Verifique los items a registrar"); return; }
        if (Number(devolutionState.vehicleID) <= 0) { toast.error("Verifique el vehículo"); return; }
        setIsSaving(true);
        try {
            const r = await sendDevolutionData(devolutionState);
            if (r.status === 201) toast.success("Operación registrada con éxito!");
            else toast.error("Ocurrió algún error");
            setDevolutionState(initialState);
        } catch { toast.error("Error al registrar la devolución"); }
        finally { setIsSaving(false); }
    };

    const hasNegativeStock = devolutionState.comparatives.some((item) => item.stockA < 0);
    const canSave = !isSaving && !hasNegativeStock && devolutionState.comparatives.length > 0 && Number(devolutionState.vehicleID) > 0;

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Devoluciones
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Distribuciones</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Devolución</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4 space-y-4">

                    {/* Controls card */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-undo text-red-500"></i>
                                Configuración de devolución
                            </h2>
                        </div>
                        <div className="px-5 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">

                                {/* Vehículo origen */}
                                <div>
                                    <label className={`${labelCls} text-blue-600 dark:text-blue-400`}>
                                        <i className="fas fa-truck mr-1"></i>Vehículo Origen
                                    </label>
                                    <select
                                        name="vehicleID"
                                        value={devolutionState.vehicleID}
                                        onChange={(e) => setDevolutionState({
                                            ...devolutionState,
                                            vehicleID: e.target.value,
                                            vehicleLicensePlate: e.nativeEvent.target[e.nativeEvent.target.selectedIndex].text,
                                        })}
                                        className={`${inputCls} text-blue-700 dark:text-blue-300 font-medium`}
                                    >
                                        <option value="0">Elegir...</option>
                                        {vehicles.map((item, i) => (
                                            <option key={i} value={item.vehicleID}>
                                                {item.vehicleLicensePlate} | {item.gang.gangName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Almacén destino */}
                                <div>
                                    <label className={`${labelCls} text-amber-600 dark:text-amber-400`}>
                                        <i className="fas fa-warehouse mr-1"></i>Almacén Destino
                                    </label>
                                    <select
                                        name="warehouseID"
                                        value={devolutionState.warehouseID}
                                        onChange={(e) => {
                                            const found = warehouses.find((w) => w.warehouseID == e.target.value);
                                            if (found) setDevolutionState({
                                                ...devolutionState,
                                                warehouseID: found.warehouseID,
                                                warehouseName: found.warehouseName,
                                                userID: auth.data.user_id,
                                            });
                                        }}
                                        className={`${inputCls} text-amber-700 dark:text-amber-300 font-medium`}
                                    >
                                        <option value="0">Elegir...</option>
                                        {warehouses.map((item, i) => (
                                            <option key={i} value={item.warehouseID}>{warehouseLabel(item)}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Fecha */}
                                <div>
                                    <label className={labelCls}><i className="fas fa-calendar mr-1"></i>Fecha</label>
                                    <input
                                        type="date"
                                        name="supplyingDate"
                                        value={devolutionState.supplyingDate}
                                        onChange={(e) => setDevolutionState({ ...devolutionState, supplyingDate: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>

                                {/* Devolver */}
                                <div className="space-y-2">
                                    <button
                                        type="button"
                                        disabled={!canSave}
                                        onClick={handleClickBtnSave}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                                    >
                                        {isSaving
                                            ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                            : <i className="fas fa-undo"></i>}
                                        {isSaving ? "Registrando..." : "Devolver"}
                                    </button>
                                    {hasNegativeStock && (
                                        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                            <i className="fas fa-exclamation-triangle"></i>
                                            Stock insuficiente en algún producto
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Products table */}
                    {devolutionState.comparatives.length > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                    Productos a devolver
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {devolutionState.comparatives.length} productos
                                </p>
                            </div>

                            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto bg-white dark:bg-gray-900">
                                <table className="min-w-full text-sm border-separate border-spacing-0">
                                    <thead className="sticky top-0 z-10">
                                        <tr>
                                            <th className={thCls} style={{ width: "5%" }}>Código</th>
                                            <th className={thCls} style={{ width: "8%" }}>Clasif.</th>
                                            <th className={thCls} style={{ width: "9%" }}>SKU</th>
                                            <th className={`${thCls} text-left`} style={{ width: "22%" }}>Producto</th>
                                            <th className={thCls} style={{ width: "9%" }}>P. Venta</th>
                                            <th className={`${thCls} text-blue-700 dark:text-blue-300`} style={{ width: "9%" }}>
                                                Stock Vehículo
                                                <br /><span className="text-[10px] font-normal text-gray-400">({devolutionState.vehicleLicensePlate.split(" ").pop()})</span>
                                            </th>
                                            <th className={`${thCls} text-amber-700 dark:text-amber-300`} style={{ width: "9%" }}>
                                                Stock Almacén
                                                <br /><span className="text-[10px] font-normal text-gray-400">({devolutionState.warehouseName.split(" ").pop()})</span>
                                            </th>
                                            <th className={thCls} style={{ width: "29%" }}>Cantidad a Devolver</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devolutionState.comparatives
                                            .sort((a, b) => a.productCode > b.productCode ? 1 : -1)
                                            .map((item, index) => {
                                                const hasQty = Number(item.quantity) > 0;
                                                return (
                                                    <tr key={index} className={hasQty ? "bg-amber-50 dark:bg-amber-900/10" : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40"}>
                                                        <td className={`${tdCls} text-center font-bold text-gray-700 dark:text-gray-300`}>{item.productCode}</td>
                                                        <td className={`${tdCls} text-center`}>
                                                            <span className="inline-block rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-medium px-2 py-0.5">
                                                                {item.productClassificationDisplay}
                                                            </span>
                                                        </td>
                                                        <td className={`${tdCls} text-center font-mono text-gray-600 dark:text-gray-400`}>{item.productSku}</td>
                                                        <td className={`${tdCls} text-left font-medium text-gray-800 dark:text-gray-200`}>{item.productSaleName}</td>
                                                        <td className={`${tdCls} text-right font-semibold text-gray-700 dark:text-gray-300`}>
                                                            S/ {Number(item.priceSale).toFixed(2)}
                                                        </td>
                                                        <td className={`${tdCls} text-center font-bold text-lg text-blue-700 dark:text-blue-300 ${item.stockA < 0 ? "bg-red-50 dark:bg-red-900/20" : ""}`}>
                                                            {item.stockA}
                                                        </td>
                                                        <td className={`${tdCls} text-center font-bold text-lg text-amber-700 dark:text-amber-300`}>
                                                            {item.stockB}
                                                        </td>
                                                        <td className={tdCls}>
                                                            {item.remainingQuantityInTariffs.map((tariffObj, ti) => (
                                                                <div key={ti} className="flex items-center gap-1 mb-1 last:mb-0">
                                                                    <span className={`flex-shrink-0 w-10 text-center rounded text-xs font-bold py-1 ${tariffObj.unitName === "CJ" ? "bg-red-500 text-white" : "bg-gray-500 text-white"}`}>
                                                                        {tariffObj.unitName}
                                                                    </span>
                                                                    <input
                                                                        type="number"
                                                                        onWheel={(e) => e.target.blur()}
                                                                        value={tariffObj.portion}
                                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            if (Number(val) >= 0 && Number(val) <= Number(item.fixedStockA) / tariffObj.quantityMinimum) {
                                                                                setDevolutionState((prev) => ({
                                                                                    ...prev,
                                                                                    comparatives: prev.comparatives.map((obj) => {
                                                                                        if (obj.productID === item.productID) {
                                                                                            obj.remainingQuantityInTariffs = item.remainingQuantityInTariffs.map((tariff) => {
                                                                                                if (Number(tariff.productTariffID) === Number(tariffObj.productTariffID)) {
                                                                                                    item.quantity = item.quantity - Number(tariff.portion) * tariff.quantityMinimum;
                                                                                                    if (Number(val) > 0) item.quantity = item.quantity + Number(val) * tariff.quantityMinimum;
                                                                                                    tariff.portion = val;
                                                                                                }
                                                                                                return tariff;
                                                                                            });
                                                                                            item.stockA = item.fixedStockA - item.quantity;
                                                                                            item.stockB = item.fixedStockB + item.quantity;
                                                                                        }
                                                                                        return obj;
                                                                                    }),
                                                                                }));
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {devolutionState.comparatives.length === 0 && (Number(devolutionState.vehicleID) > 0 || Number(devolutionState.warehouseID) > 0) && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                            <div className="py-16 text-center">
                                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                                    <i className="fas fa-box-open text-gray-400 dark:text-gray-500 text-2xl"></i>
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin productos</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    Seleccione vehículo y almacén para cargar los productos.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
